/**
 * DemoAccessPage — /demo/:code
 *
 * The public page a prospect lands on after the superadmin shares
 * their generated demo URL. Validates the code on mount, then lets
 * the visitor pick one of four roles and lands them on /welcome.
 *
 * 50/50 layout matches LoginPage: brand MP4 on the left, panel on
 * the right.
 */
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { decidePostLoginPath } from "../utils/postLoginRedirect";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON     = "#00FF99";

const ROLES = [
  {
    role:        "staff",
    title:       "Staff",
    person:      "Aisha Patel",
    description: "See KweryAI from an employee's perspective — leave, attendance, payroll and KIRA.",
  },
  {
    role:        "manager",
    title:       "Manager",
    person:      "Raj Mehta",
    description: "Manage your team, approve requests and get AI-powered team insights.",
  },
  {
    role:        "admin",
    title:       "Admin",
    person:      "Sarah Chen",
    description: "Full org visibility — configure settings, manage users, run payroll.",
  },
  {
    role:        "executive",
    title:       "Executive",
    person:      "James Wright",
    description: "Board-level KPIs, workforce health and executive briefings from KIRA.",
  },
];

function formatTimeRemaining(iso) {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  const now    = Date.now();
  const diff   = target - now;
  if (Number.isNaN(target) || diff <= 0) return "expired";
  const hours   = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours >= 48) return `${Math.floor(hours / 24)} days`;
  if (hours >= 1)  return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export default function DemoAccessPage() {
  const { code }                = useParams();
  const { login }               = useContext(AuthContext);

  const [session, setSession]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState(null);
  const [logging, setLogging]   = useState(null);   // which role is logging in

  // Validate the code on mount.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!code) {
        setError("Missing demo code in URL.");
        setLoading(false);
        return;
      }
      try {
        const res  = await fetch(
          `${API_BASE}/api/demo/access/${encodeURIComponent(code)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = body?.detail || `HTTP ${res.status}`;
          if (!cancelled) setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } else {
          if (!cancelled) setSession(body);
        }
      } catch {
        if (!cancelled) setError("Network error — couldn't reach the demo service.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [code]);

  const handleLogin = async (role) => {
    if (logging) return;
    setLogging(role); setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/demo/access/${encodeURIComponent(code)}/login`,
        {
          method:      "POST",
          headers:     { "Content-Type": "application/json" },
          body:        JSON.stringify({ role }),
          credentials: "include",
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || `HTTP ${res.status}`;
        setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        setLogging(null);
        return;
      }
      if (data.token && data.user) {
        login(data.token, data.user, data.refresh_token);
        try {
          sessionStorage.removeItem("kira_welcomed");
          sessionStorage.setItem("kira_from_login", "true");
          // Mark this session as a demo-code visit so the rest of the
          // app can hide module tabs the session doesn't include.
          sessionStorage.setItem("demo_session_code",  data.code  || code);
          sessionStorage.setItem("demo_session_label", data.session_label || "");
          sessionStorage.setItem("demo_session_modules", JSON.stringify(data.modules || []));
        } catch {}
        const dest = await decidePostLoginPath({
          user: data.user,
          token: data.token,
          is_first_login: data.is_first_login,
        });
        // eslint-disable-next-line no-console
        console.log("[login] navigating to:", dest);
        window.location.href = dest;
      } else {
        setError("Login response missing token.");
        setLogging(null);
      }
    } catch {
      setError("Network error during login.");
      setLogging(null);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#030303" }}>

      {/* ─────────────── LEFT — brand video ─────────────── */}
      <div
        className="kwery-demo-left"
        style={{
          width:      "50%",
          position:   "relative",
          overflow:   "hidden",
          background: "#030303",
          display:    "none",
        }}
      >
        <video
          autoPlay muted playsInline loop
          src="/media/kira-intro.mp4"
          style={{
            position:  "absolute",
            inset:     0,
            width:     "100%",
            height:    "100%",
            objectFit: "cover",
          }}
        />
        <div style={{ position: "absolute", inset: 0, background: "rgba(3,3,3,0.30)" }} />
        <div style={{ position: "absolute", top: 40, left: 40, zIndex: 2 }}>
          <div style={{
            fontFamily:    "'Syne', sans-serif",
            fontSize:      22,
            fontWeight:    700,
            color:         NEON,
            letterSpacing: 1,
          }}>
            KweryAI
          </div>
          <div style={{
            fontSize:       11,
            color:          "rgba(0,255,153,0.5)",
            letterSpacing:  2,
            textTransform:  "uppercase",
            marginTop:      4,
          }}>
            Workplace Intelligence
          </div>
        </div>
      </div>

      {/* ─────────────── RIGHT — demo access panel ─────────────── */}
      <div style={{
        flex:           "1 1 50%",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "48px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: 560 }}>

          {/* Header */}
          <h1 style={{
            color:      "#fff",
            fontSize:   34,
            lineHeight: 1.2,
            fontWeight: 700,
            margin:     0,
          }}>
            Watch our <span style={{ color: NEON }}>features in action</span>
          </h1>
          <p style={{
            color:    "rgba(255,255,255,0.55)",
            fontSize: 14,
            marginTop: 10,
          }}>
            Select a role to explore KweryAI from every perspective.
          </p>

          {/* Code validation status */}
          {loading && (
            <div style={{
              marginTop:   24,
              padding:     "14px 16px",
              borderRadius: 12,
              border:      "1px solid rgba(255,255,255,0.08)",
              background:  "rgba(255,255,255,0.03)",
              color:       "rgba(255,255,255,0.6)",
              fontSize:    13,
              display:     "flex",
              alignItems:  "center",
              gap:         10,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: "50%",
                border: `2px solid ${NEON}`, borderTopColor: "transparent",
                animation: "demo-spin 0.7s linear infinite",
              }} />
              Validating demo code…
            </div>
          )}

          {!loading && error && (
            <div style={{
              marginTop:    24,
              padding:      "14px 16px",
              borderRadius: 12,
              border:       "1px solid rgba(255,77,77,0.35)",
              background:   "rgba(255,77,77,0.10)",
              color:        "#ffb4b4",
              fontSize:     13,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Can't open this demo
              </div>
              <div>{error}</div>
              <div style={{ marginTop: 10, color: "rgba(255,255,255,0.4)" }}>
                If this is unexpected, ask the person who shared the link to issue a new code.
              </div>
            </div>
          )}

          {!loading && !error && session && (
            <>
              {session.prospect_name && (
                <div style={{
                  marginTop:    24,
                  padding:      "12px 16px",
                  borderRadius: 12,
                  border:       `1px solid ${NEON}33`,
                  background:   "rgba(0,255,153,0.06)",
                  color:        "#dafff0",
                  fontSize:     13,
                }}>
                  Welcome, <span style={{ color: NEON, fontWeight: 600 }}>{session.prospect_name}</span>
                  {session.prospect_company ? ` · ${session.prospect_company}` : ""}
                </div>
              )}

              {/* Role cards */}
              <div style={{
                marginTop:    24,
                display:      "grid",
                gridTemplateColumns: "1fr",
                gap:          12,
              }}>
                {ROLES.map((r) => {
                  const isLogging = logging === r.role;
                  const disabled  = !!logging;
                  return (
                    <button
                      key={r.role}
                      type="button"
                      onClick={() => handleLogin(r.role)}
                      disabled={disabled}
                      className="demo-role-card"
                      style={{
                        textAlign:    "left",
                        padding:      "16px 18px",
                        borderRadius: 14,
                        background:   "rgba(0,255,153,0.04)",
                        border:       "1px solid rgba(0,255,153,0.15)",
                        color:        "#fff",
                        cursor:       disabled ? "default" : "pointer",
                        opacity:      disabled && !isLogging ? 0.4 : 1,
                        transition:   "border-color .15s, background .15s",
                        position:     "relative",
                      }}
                    >
                      <div style={{
                        display:     "flex",
                        alignItems:  "baseline",
                        gap:         10,
                        marginBottom: 6,
                      }}>
                        <span style={{
                          fontSize:      11,
                          letterSpacing: 2,
                          textTransform: "uppercase",
                          color:         NEON,
                          fontWeight:    700,
                        }}>{r.title}</span>
                        <span style={{
                          fontSize: 14,
                          color:    "#fff",
                          fontWeight: 600,
                        }}>{r.person}</span>
                      </div>
                      <div style={{
                        fontSize: 13,
                        color:    "rgba(255,255,255,0.55)",
                        lineHeight: 1.5,
                      }}>
                        {r.description}
                      </div>
                      {isLogging && (
                        <div style={{
                          position: "absolute", top: 14, right: 14,
                          width: 14, height: 14, borderRadius: "50%",
                          border: `2px solid ${NEON}`, borderTopColor: "transparent",
                          animation: "demo-spin 0.7s linear infinite",
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{
            marginTop:  32,
            textAlign:  "center",
            color:      "rgba(255,255,255,0.30)",
            fontSize:   11,
          }}>
            Powered by KweryAI
            {session?.expires_at && (
              <>
                {" · "}
                <span>This demo expires in {formatTimeRemaining(session.expires_at)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .kwery-demo-left { display: block !important; }
        }
        @keyframes demo-spin {
          to { transform: rotate(360deg); }
        }
        .demo-role-card:hover:not(:disabled) {
          border-color: rgba(0,255,153,0.55) !important;
          background:   rgba(0,255,153,0.08) !important;
        }
      `}</style>
    </div>
  );
}
